import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, useScroll, useSpring } from "framer-motion";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, CalendarDays, Clock3, Facebook, Link2, Linkedin, MessageCircle, User } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/api";
import { useLang } from "@/i18n";
import { BlogCard } from "@/components/BlogCard";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";
import { useContact } from "@/components/ContactModal";
import { ArrowUpRight } from "lucide-react";

function ShareRow({ title }) {
  const { t } = useLang();
  const url = typeof window !== "undefined" ? window.location.href : "";
  const open = (link) => window.open(link, "_blank", "noopener,noreferrer");
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(t("article.copied"));
    } catch {
      toast.error(t("modal.error"));
    }
  };
  return (
    <div className="share-row" data-testid="share-row">
      <span>{t("article.share")}</span>
      <button className="share-btn" onClick={() => open(`https://wa.me/?text=${encodeURIComponent(`${title} — RKJ GST Consultancy ${url}`)}`)} data-testid="share-whatsapp" aria-label="Share on WhatsApp"><MessageCircle size={17} /></button>
      <button className="share-btn" onClick={() => open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`)} data-testid="share-facebook" aria-label="Share on Facebook"><Facebook size={17} /></button>
      <button className="share-btn" onClick={() => open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`)} data-testid="share-linkedin" aria-label="Share on LinkedIn"><Linkedin size={17} /></button>
      <button className="share-btn" onClick={copy} data-testid="share-copy" aria-label="Copy link"><Link2 size={17} /></button>
    </div>
  );
}

export default function BlogDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const { open: openContact } = useContact();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [missing, setMissing] = useState(false);
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });

  useEffect(() => {
    setPost(null);
    setMissing(false);
    api.get(`/blogs/${id}`)
      .then((r) => setPost(r.data))
      .catch(() => setMissing(true));
    api.get("/blogs")
      .then((r) => setRelated((r.data || []).filter((b) => b.id !== id).slice(0, 2)))
      .catch(() => {});
  }, [id]);

  if (missing) {
    return (
      <div className="article-missing section-wrap" data-testid="article-not-found">
        <h1>{t("article.notFound")}</h1>
        <Link to="/blog" className="btn btn-dark" data-testid="article-not-found-back">{t("article.notFoundCta")}</Link>
      </div>
    );
  }

  if (!post) {
    return <div className="article-missing section-wrap" data-testid="article-loading"><p>{t("article.loading")}</p></div>;
  }

  const title = lang === "hi" && post.title_hi ? post.title_hi : post.title;
  const content = lang === "hi" && post.content_hi ? post.content_hi : post.content || post.excerpt;
  const catLabel = t("cats")[post.category] ?? post.category;

  return (
    <div data-testid="article-page">
      <Seo title={`${post.title} — RKJ GST Consultancy`} description={post.excerpt} />
      <motion.div className="read-progress" style={{ scaleX }} />
      <article className="section-wrap article-wrap">
        <Reveal>
          <Link to="/blog" className="back-link" data-testid="back-to-blog"><ArrowLeft size={16} /> {t("article.back")}</Link>
        </Reveal>
        <header className="article-hero">
          <Reveal delay={0.05}>
            <div className="article-hero-top">
              <Link className="chip" to={`/blog?cat=${encodeURIComponent(post.category)}`} data-testid="article-category">{catLabel}</Link>
            </div>
          </Reveal>
          <Reveal delay={0.12}><h1 data-testid="article-title">{title}</h1></Reveal>
          <Reveal delay={0.2}>
            <div className="article-meta" data-testid="article-meta">
              <span><User size={15} /> {t("blog.by")} {post.author || "RKJ Desk"}</span>
              <span><CalendarDays size={15} /> {post.date}</span>
              <span><Clock3 size={15} /> {post.read_minutes || 4} {t("article.min")}</span>
            </div>
          </Reveal>
          <Reveal delay={0.26}><ShareRow title={post.title} /></Reveal>
        </header>
        <Reveal delay={0.15}>
          <div className="article-body" data-testid="article-content">
            <ReactMarkdown>{content}</ReactMarkdown>
          </div>
        </Reveal>
        {(post.tags || []).length > 0 && (
          <Reveal>
            <div className="tag-row" data-testid="article-tags">
              <span>{t("article.tags")}</span>
              {post.tags.map((tag) => <i className="tag" key={tag}>{tag}</i>)}
            </div>
          </Reveal>
        )}
        <Reveal><ShareRow title={post.title} /></Reveal>
        <Reveal>
          <div className="article-cta" data-testid="article-cta">
            <p>{t("banner.t1")} {t("banner.t2")}</p>
            <button className="btn btn-gold" onClick={openContact} data-testid="article-consultation-button">
              {t("banner.cta")} <ArrowUpRight size={16} />
            </button>
          </div>
        </Reveal>
      </article>
      {related.length > 0 && (
        <section className="section-wrap related-section" data-testid="related-articles">
          <Reveal><h2>{t("article.related")}</h2></Reveal>
          <div className="blog-grid">
            {related.map((b, i) => <BlogCard key={b.id} post={b} index={i} />)}
          </div>
        </section>
      )}
    </div>
  );
}
